Ext.define('SA.data.GeographicUnits', {
    singleton: true,

    fixedUnits: {
        USA: ['usa', 'usa:state', 'usa:county', 'usa:place', 'usa:zipCode', 'usa:censusTract', 'usa:censusBlockGroup'],
        CAN: ['can', 'can:province', 'can:cmaca', 'can:cd', 'can:fsa', 'can:csd', 'can:ct', 'can:da']
    },

    countryFor(geographicUnit) {
        var countriesCache = this.countriesCache;

        if (!countriesCache) {
            countriesCache = this.countriesCache = {};

            SA.Institution.eachCountry(country => {
                var countryCode = country.getCode();

                this.findIds({country: countryCode, includeCountry: true}).forEach(unit => {
                    countriesCache[unit] = countryCode;
                });
            });
        }

        return countriesCache[geographicUnit];
    },

    find(criteria) {
        criteria = criteria || {};

        var countryFilter = criteria.country,
            idsFilter = criteria.units,
            maximum = criteria.maximum,
            minimum = criteria.minimum,
            includeCountry = criteria.includeCountry,
            censusReleaseFilter = criteria.censusRelease,
            units = [];

        if (maximum) {
            maximum = this.get(maximum);
        }

        if (minimum) {
            minimum = this.get(minimum);
        }

        SA.Institution.eachCountry(country => {
            if (countryFilter && countryFilter !== country.getCode()) {
                return;
            }

            var censusRelease = censusReleaseFilter || country.getLatestCensusRelease(),
                unitsForCountry = country.getGeographicUnitsForCensusRelease(censusRelease),
                countryUnit = country.getGeographicUnit(),
                indexOf;

            if (maximum) {
                indexOf = unitsForCountry.indexOf(maximum);

                if (indexOf === -1) {
                    unitsForCountry = [];
                }
                else {
                    unitsForCountry = unitsForCountry.slice(indexOf);
                }
            }

            if (minimum) {
                indexOf = unitsForCountry.findIndex(unit => minimum.geographicUnit === unit.geographicUnit);

                unitsForCountry = unitsForCountry.slice(0, indexOf + 1);
            }

            if (!includeCountry) {
                unitsForCountry = unitsForCountry.filter(unit => unit.geographicUnit !== countryUnit);
            }

            unitsForCountry.forEach(unit => {
                if (!idsFilter || idsFilter.includes(unit.id)) {
                    units.push(unit);
                }
            });
        });

        return units;
    },

    findIds(criteria) {
        return this.find(criteria).map(unit => unit.id);
    },

    forCountry(country) {
        return this.find({country});
    },

    forCountryAndCensusRelease(country, censusRelease) {
        return this.find({country, censusRelease});
    },

    forCountryAndLatestCensusRelease(countryCode) {
        var country = SA.Institution.getCountry(countryCode);

        return this.forCountryAndCensusRelease(countryCode, country.getLatestCensusRelease());
    },

    get(units) {
        var cache = this.idsCache;

        if (Array.isArray(units)) {
            return this.find({units});
        }

        if (!cache) {
            cache = this.idsCache = {};

            this.find({includeCountry: true}).forEach(unit => {
                cache[unit.id] = unit;
            });
        }

        return cache[units];
    },

    getAllowedGeographicUnitsForLocationAndAttributes(location, attributes) {
        var locationUnits = this.getAllowedIdsForLocation(location),
            attributeUnits = this.getAllowedIdsForAttributes(attributes),
            units = Ext.Array.intersect(locationUnits, attributeUnits),
            maximum = SA.data.LocationsCache.getGeographicUnit(location);

        return this.find({includeCountry: true, maximum, units});
    },

    getAllowedIds({location, attribute, attributes}) {
        location = SA.data.LocationsCache.toLocationSeries(location);
        attributes = Ext.Array.from(attribute || attributes);

        if (attributes.length === 0) {
            return this.getAllowedIdsForLocation(location);
        }
        else if (!location) {
            return this.getAllowedIdsForAttributes(attributes);
        }

        return this.getAllowedGeographicUnitsForLocationAndAttributes(location, attributes).map(({id}) => id);
    },

    getAllowedIdsForAttribute(attributeId) {
        var attribute = SA.data.AttributesCache.get(attributeId),
            censusReleaseUnits = this.idsForCountryAndCensusRelease(attribute.country, attribute.censusRelease);

        return Ext.Array.intersect(attribute.geographicUnits, censusReleaseUnits);
    },

    getAllowedIdsForAttributes(attributes) {
        return Ext.Array.intersect(...attributes.map(this.getAllowedIdsForAttribute, this));
    },

    getAllowedIdsForLocation(location) {
        var locationUnit = SA.data.LocationsCache.getGeographicUnit(location),
            country = this.countryFor(locationUnit),
            units = this.findIds({country, maximum: locationUnit});

        if (locationUnit === 'usa') {
            Ext.Array.remove(units, 'usa:censusBlockGroup'); 
        }

        return units;
    },

    getDefaultDiscoverableUnitsForCountry(country) {
        var fixedUnits = this.getFixedDiscoverableUnitsForCountry(country),
            includeDMAs = this.getOptionalDiscoverableUnitsForCountry(country).includes('usa:dma');

        return includeDMAs ? [...fixedUnits, 'usa:dma'] : fixedUnits;
    },

    getFixedDiscoverableUnitsForCountry(country) {
        return this.fixedUnits[country].slice();
    },

    getOptionalDiscoverableUnitsForCountry(country) {
        var latestCensusRelease = SA.Institution.getCountry(country).getLatestCensusRelease(),
            allUnits = this.findIds({country, includeCountry: true, censusRelease: latestCensusRelease}),
            fixedUnits = this.getFixedDiscoverableUnitsForCountry(country);

        return Ext.Array.difference(allUnits, fixedUnits);
    },

    getSmallestUnitForCountryAndScale(country, scale) {
        var scaleFilter = ({maximumScale}) => maximumScale == null || scale <= maximumScale,
            units = this.forCountryAndLatestCensusRelease(country).filter(scaleFilter);

        return SA.Array.getLast(units);
    },

    getUnitLabelForLocation(location) {
        var {type, geographicUnit} = location || {};

        if (!location) {
            return '';
        }

        if (type === 'radius') {
            return 'Radius Location';
        }

        if (type === 'combination') {
            let locations = location.locationSeries,
                unitId = SA.data.LocationsCache.get(locations[0]).geographicUnit,
                unit = this.get(unitId),
                locationCount = locations.length;

            return locationCount === 1 ? unit.singularName : `${locationCount} ${unit.name}`;
        }

        return this.get(geographicUnit).singularName;
    },

    idsForCountry(country) {
        return this.findIds({country});
    },

    idsForCountryAndCensusRelease(country, censusRelease) {
        return this.findIds({country, censusRelease});
    },

    smallestIdFor(country) {
        var cache = this.smallestCache || (this.smallestCache = {});

        return cache[country] || (cache[country] = this.idsForCountry(country).pop());
    }
});
